import React from 'react';
import { Card, Box, Typography, Tooltip } from '@mui/material';
import { COLOR, FUENTE } from '../../Recepcion/reportes/tokens';

/**
 * Cómo terminaron las presentaciones del período, todas juntas.
 *
 * Va como barra única apilada y no como torta: los resultados son un recorrido
 * (pendiente → ingresado → favorable), y una torta los presentaría como categorías sueltas del
 * mismo rango. El backend ya los devuelve en ese orden.
 *
 * Los colores dicen qué pasó, no de qué categoría se trata: gris lo que no se resolvió, azul lo
 * que entró, verde lo que salió bien, rojo lo que salió mal, ámbar lo que quedó en el medio.
 *
 * @param {{data: Array<{resultado: string, cantidad: number}>}} props
 */

const COLOR_RESULTADO = {
    PENDIENTE: COLOR.gris,
    INGRESADO: COLOR.azul,
    CONDICIONAL: COLOR.ambar,
    NO_PRESENTADO: COLOR.grisFrio,
    FAVORABLE: COLOR.verde,
    RECHAZADO: COLOR.rojo,
    DESFAVORABLE: COLOR.rojoClaro,
};

const ETIQUETA = {
    PENDIENTE: 'Sin resultado',
    INGRESADO: 'Ingresado',
    CONDICIONAL: 'Condicional',
    NO_PRESENTADO: 'No presentado',
    FAVORABLE: 'Favorable',
    RECHAZADO: 'Rechazado',
    DESFAVORABLE: 'Desfavorable',
};

// PENDIENTE no es un desenlace: es lo que todavía no volvió. Se muestra aparte del resto.
const esResuelto = (resultado) => resultado !== 'PENDIENTE';

const ResultadosDistribucion = ({ data }) => {
    const filas = (data || []).filter((f) => f.cantidad > 0);
    const total = filas.reduce((acc, f) => acc + f.cantidad, 0);
    const resueltas = filas.filter((f) => esResuelto(f.resultado)).reduce((acc, f) => acc + f.cantidad, 0);

    return (
        <Card variant="outlined" sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: 'none', padding: '18px 20px' }}>
            <Typography sx={{ fontFamily: FUENTE, fontSize: 15, fontWeight: 700, color: COLOR.negro, marginBottom: '2px' }}>
                Cómo terminaron las presentaciones
            </Typography>
            <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '16px' }}>
                {total > 0
                    ? `${total} presentaciones · ${resueltas} con resultado registrado`
                    : 'Sin presentaciones en el período.'}
            </Typography>

            {total === 0 ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisFrio, padding: '24px 0', textAlign: 'center' }}>
                    Sin datos en el período.
                </Typography>
            ) : (
                <>
                    <Box sx={{ display: 'flex', height: 30, borderRadius: 6, overflow: 'hidden', marginBottom: '14px' }}>
                        {filas.map((f) => (
                            <Tooltip key={f.resultado} title={`${ETIQUETA[f.resultado] || f.resultado}: ${f.cantidad}`} arrow>
                                <Box
                                    sx={{
                                        width: `${(f.cantidad / total) * 100}%`,
                                        background: COLOR_RESULTADO[f.resultado] || COLOR.azul,
                                        cursor: 'default',
                                    }}
                                />
                            </Tooltip>
                        ))}
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px' }}>
                        {filas.map((f) => (
                            <Box key={f.resultado} sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: COLOR_RESULTADO[f.resultado] || COLOR.azul, flexShrink: 0 }} />
                                <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto }}>
                                    {ETIQUETA[f.resultado] || f.resultado}
                                </Typography>
                                <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, fontWeight: 700, color: COLOR.negro, fontVariantNumeric: 'tabular-nums' }}>
                                    {f.cantidad}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </>
            )}
        </Card>
    );
};

export default ResultadosDistribucion;
